import React, { useMemo } from 'react';
import { Box, Container, Typography, Paper, Chip, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useFamily } from '../../context/FamilyContext';
import { useSkillGraph } from '../../hooks/useSkillGraph';
import { useLearnerMastery } from '../../hooks/useLearnerMastery';

const STATUS_COLOR: Record<string, 'default' | 'warning' | 'info' | 'success'> = {
  'not-attempted': 'default',
  emerging: 'warning',
  developing: 'info',
  secure: 'success',
};

/**
 * Lightweight prerequisite visualization: skills grouped into columns by
 * topological depth (root skills with no prerequisites on the left, skills
 * that depend on them to the right). No new charting dependency - a full
 * graph-drawing library was explicitly out of scope for this phase (see
 * IMPLEMENTATION_STATE.md Phase 4 entry, "SkillTree ... stretch, not
 * blocking"); this is a CSS-column view of the same prerequisite data
 * MasteryMap already fetches via useSkillGraph.
 */
const SkillTree: React.FC = () => {
  const navigate = useNavigate();
  const { learners } = useFamily();
  const learner = learners[0] || null;
  const { nodes, edges, loading: graphLoading } = useSkillGraph();
  const { mastery, loading: masteryLoading } = useLearnerMastery(learner?._id || null);

  const columns = useMemo(() => {
    if (nodes.length === 0) return [];

    // depth(node) = 1 + max(depth(prerequisite)) ; roots (no incoming edge as target) = depth 0
    const prereqsByNode = new Map<string, string[]>();
    edges.forEach(([prereqId, dependentId]) => {
      const list = prereqsByNode.get(dependentId) || [];
      list.push(prereqId);
      prereqsByNode.set(dependentId, list);
    });

    const depthCache = new Map<string, number>();
    const computeDepth = (nodeId: string, visiting = new Set<string>()): number => {
      if (depthCache.has(nodeId)) return depthCache.get(nodeId)!;
      if (visiting.has(nodeId)) return 0; // guard against unexpected cycles
      const prereqs = prereqsByNode.get(nodeId) || [];
      if (prereqs.length === 0) {
        depthCache.set(nodeId, 0);
        return 0;
      }
      visiting.add(nodeId);
      const depth = 1 + Math.max(...prereqs.map((p) => computeDepth(p, visiting)));
      visiting.delete(nodeId);
      depthCache.set(nodeId, depth);
      return depth;
    };

    const maxDepth = Math.max(...nodes.map((n) => computeDepth(n.id)));
    const cols: typeof nodes[] = Array.from({ length: maxDepth + 1 }, () => []);
    nodes.forEach((n) => cols[computeDepth(n.id)].push(n));
    return cols;
  }, [nodes, edges]);

  const loading = graphLoading || masteryLoading;
  const masteryBySkillId = useMemo(
    () => new Map(mastery.map((m) => [m.skillId._id, m.status])),
    [mastery]
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 'bold' }}>
        Skill Prerequisites
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Skills on the left are foundational; skills to the right build on them.
      </Typography>

      <Box sx={{ display: 'flex', gap: 3, overflowX: 'auto', pb: 2 }}>
        {columns.map((col, depth) => (
          <Box key={depth} sx={{ minWidth: 220, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {depth === 0 ? 'Foundational' : `Builds on level ${depth}`}
            </Typography>
            {col.map((node) => (
              <Paper
                key={node.id}
                sx={{ p: 1.5, cursor: learner ? 'pointer' : 'default' }}
                onClick={() => learner && navigate(`/dashboard/skills/${node.id}?learnerId=${learner._id}`)}
              >
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  {node.name}
                </Typography>
                <Chip
                  size="small"
                  label={masteryBySkillId.get(node.id) || 'not-attempted'}
                  color={STATUS_COLOR[masteryBySkillId.get(node.id) || 'not-attempted']}
                />
              </Paper>
            ))}
          </Box>
        ))}
      </Box>
    </Container>
  );
};

export default SkillTree;
